import { supabase } from './supabaseClient';

const BUCKET = 'media';

// ---- Upload zdjęć do Storage ----
const PL = { ą:'a', ć:'c', ę:'e', ł:'l', ń:'n', ó:'o', ś:'s', ź:'z', ż:'z', Ą:'A', Ć:'C', Ę:'E', Ł:'L', Ń:'N', Ó:'O', Ś:'S', Ź:'Z', Ż:'Z' };
function safeName(name) {
  return name
    .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (c) => PL[c] || c)
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]/g, '_');
}

// Przygotuj zdjęcie do sieci przed wysyłką:
//  - HEIC/HEIF (iPhone) -> odrzuć z czytelnym komunikatem (przeglądarki ich nie wyświetlają),
//  - zmniejsz do max 2000 px dłuższego boku,
//  - PNG -> WebP (zachowuje przezroczystość), pozostałe -> JPEG.
// Jeśli re-kodowanie się nie uda lub nie daje zysku, wysyłany jest oryginał.
const MAX_EDGE = 2000;

async function toWebSafeImage(file) {
  if (/\.(heic|heif)$/i.test(file.name) || /image\/hei[cf]/i.test(file.type || '')) {
    throw new Error(
      'Zdjęcia HEIC/HEIF (format iPhone) nie są wyświetlane przez większość przeglądarek. ' +
      'Zapisz zdjęcie jako JPG (iPhone: Ustawienia → Aparat → Formaty → „Najbardziej zgodne") i wgraj ponownie.'
    );
  }
  if (!/^image\//.test(file.type || '')) return file;
  if (file.type === 'image/gif') return file; // zachowaj animacje

  let bitmap;
  try { bitmap = await createImageBitmap(file); } catch { return file; }

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  const isPng = file.type === 'image/png';
  const mime = isPng ? 'image/webp' : 'image/jpeg';
  const blob = await new Promise((res) => canvas.toBlob(res, mime, 0.86));
  if (!blob) return file;
  if (scale === 1 && blob.size >= file.size) return file; // brak zysku — zostaw oryginał

  const base = file.name.replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.${isPng ? 'webp' : 'jpg'}`, { type: mime });
}

export async function uploadImage(file, folder = 'uploads') {
  const prepared = await toWebSafeImage(file);
  const key = `${folder}/${Date.now()}-${safeName(prepared.name)}`;
  const { error } = await supabase.storage.from(BUCKET).upload(key, prepared, {
    contentType: prepared.type || 'application/octet-stream',
    cacheControl: '31536000',
    upsert: true,
  });
  if (error) throw error;
  return supabase.storage.from(BUCKET).getPublicUrl(key).data.publicUrl;
}

export async function uploadImages(files, folder = 'uploads') {
  const urls = [];
  for (const f of files) urls.push(await uploadImage(f, folder));
  return urls;
}

// ---- DOGS ----
export async function saveDog(dog) {
  const row = {
    id: dog.id,
    name: dog.name,
    full_name: dog.fullName || null,
    birth_date: dog.birthDate || null,
    category: dog.category || 'females',
    origin: dog.origin || null,
    description_pl: dog.descriptionPl ?? dog.description?.pl ?? null,
    description_en: dog.descriptionEn ?? dog.description?.en ?? null,
    profile_image_url: dog.profileImage || null,
    pedigree_image_url: dog.pedigreeImage || null,
    images: dog.images || [],
    sort_order: dog.sortOrder ?? 0,
  };
  const { error } = await supabase.from('dogs').upsert(row, { onConflict: 'id' });
  if (error) throw error;
}

export async function deleteDog(id) {
  const { error } = await supabase.from('dogs').delete().eq('id', id);
  if (error) throw error;
}

// ---- LITTERS ----
export async function saveLitter(litter) {
  const row = {
    id: litter.id,
    letter: litter.letter || null,
    date: litter.date || null,
    description_pl: litter.descriptionPl ?? litter.description?.pl ?? null,
    description_en: litter.descriptionEn ?? litter.description?.en ?? null,
    presentation_image_url: litter.presentationImage || null,
    gallery: litter.gallery || [],
    sort_order: litter.sortOrder ?? 0,
  };
  const { error } = await supabase.from('litters').upsert(row, { onConflict: 'id' });
  if (error) throw error;
}

export async function deleteLitter(id) {
  const { error } = await supabase.from('litters').delete().eq('id', id);
  if (error) throw error;
}

// ---- SHOWS ----
export async function saveShow(show) {
  const row = {
    dog_name: show.dogName,
    date: show.date || null,
    location: show.location || null,
    type: JSON.stringify({ pl: show.typePl || '', en: show.typeEn || '' }),
    achievement: JSON.stringify({ pl: show.achievementPl || '', en: show.achievementEn || '' }),
    judge: show.judge || null,
  };
  if (show.id) {
    const { error } = await supabase.from('shows').update(row).eq('id', show.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('shows').insert(row);
    if (error) throw error;
  }
}

export async function deleteShow(id) {
  const { error } = await supabase.from('shows').delete().eq('id', id);
  if (error) throw error;
}

// ---- GALLERY ----
export async function addGalleryImages(urls) {
  const { data: max } = await supabase.from('gallery').select('sort_order').order('sort_order', { ascending: false }).limit(1);
  let order = (max && max[0]?.sort_order != null ? max[0].sort_order : -1) + 1;
  const rows = urls.map((url) => ({ image_url: url, sort_order: order++ }));
  const { error } = await supabase.from('gallery').insert(rows);
  if (error) throw error;
}

export async function deleteGalleryImage(id) {
  const { error } = await supabase.from('gallery').delete().eq('id', id);
  if (error) throw error;
}

// ---- WIDOCZNOŚĆ (ukryj/pokaż element bez usuwania) ----
export async function setVisible(table, id, visible) {
  const { error } = await supabase.from(table).update({ visible }).eq('id', id);
  if (error) {
    const msg = `${error.message || ''} ${error.details || ''}`;
    if (/visible/i.test(msg) && /(does not exist|schema cache|could not find|unknown column)/i.test(msg)) {
      throw new Error('Funkcja ukrywania nie jest jeszcze aktywna — uruchom migrację supabase/visibility.sql w Supabase (SQL Editor).');
    }
    throw error;
  }
}

// ---- SITE CONTENT (teksty stron) ----
export async function saveContent(rows) {
  // rows: [{ key, label, value_pl, value_en }]
  const { error } = await supabase.from('site_content').upsert(rows, { onConflict: 'key' });
  if (error) throw error;
}
